import type { Context } from "../../models/context";
import type { Data } from "../../models/data";
import { MapProvider } from "../../types/enums";
import { getGeoServiceApiKeys } from "../geo-service-api-keys";
import { MapProviderFactory } from "./map-provider-factory";
import { IMapProvider } from "./map-provider.interface";

/**
 * The map provider Setting 275 names for this brand, with its key. Pages are built before the
 * brand data lands, so they resolve from the fallback first and call refresh() once it arrives.
 */
export class ConfiguredMapProvider {
    // Several pages each build one and refresh it on brand data, so the warning is logged once per page load.
    private static readonly _warnedProviderIds = new Set<MapProvider>();

    private readonly _data: Data;
    private readonly _context: Context;
    private readonly _fallbackProviderId: MapProvider;
    private _providerId: MapProvider;
    private _apiKey: string;
    private _provider: IMapProvider;

    constructor(data: Data, context: Context, fallbackProviderId: MapProvider) {
        this._data = data;
        this._context = context;
        this._fallbackProviderId = fallbackProviderId;
        this._providerId = this._resolveProviderId();
        this._apiKey = this._resolveApiKey(this._providerId);
        this._provider = MapProviderFactory.getMapProvider(this._providerId, this._apiKey);
    }

    public get provider(): IMapProvider {
        return this._provider;
    }

    /** Returns true when the provider or its key changed, so the caller must drop its map. */
    public refresh(): boolean {
        const providerId = this._resolveProviderId();
        const apiKey = this._resolveApiKey(providerId);
        if (providerId === this._providerId && apiKey === this._apiKey)
            return false;

        this._providerId = providerId;
        this._apiKey = apiKey;
        this._provider = MapProviderFactory.getMapProvider(providerId, apiKey);
        return true;
    }

    // A provider named without a key (e.g. Mapbox before the brand data is rebuilt with its key)
    // falls back too, so the page keeps a working map instead of none.
    private _resolveProviderId(): MapProvider {
        const configuredProviderId = this._data.getBrandProperty("Settings")?.LOCSET?.mapProvider;
        if (configuredProviderId == null)
            return this._fallbackProviderId;

        if (this._resolveApiKey(configuredProviderId))
            return configuredProviderId;

        this._warnMissingApiKey(configuredProviderId);
        return this._fallbackProviderId;
    }

    private _warnMissingApiKey(providerId: MapProvider): void {
        if (ConfiguredMapProvider._warnedProviderIds.has(providerId))
            return;

        ConfiguredMapProvider._warnedProviderIds.add(providerId);
        console.warn(
            `[Maps] Setting 275 selects ${MapProvider[providerId]} but it has no API key; ` +
            `using ${MapProvider[this._fallbackProviderId]} instead.`
        );
    }

    private _resolveApiKey(providerId: MapProvider): string {
        return MapProviderFactory.getApiKey(providerId, getGeoServiceApiKeys(this._data, this._context));
    }
}
